
import { v4 as uuidv4 } from "uuid";
import { CreatureLimitation, DiceRoll, ItemLimitation } from "@dndModels/misc";
import { DataType } from "@dndModels/Types";
import { ModelBase } from "@models/UpsertableBase";

/**
 * Originally I was thinking of doing this similarly to how it's done in WoW, 
 * where each type of creature has its own loot table,
 * but that is an insane way to do it. 
 * 
 * Instead I'm going to make it so there are a number of master loot tables, 
 * where each entry has a bunch of details about the kinds of creatures, settings, etc that item belongs in,
 * and then when rolling for loot on the table you give it the relevant data, and it filters for those things, 
 * and then rolls on what's left.
 */
export class LootTable extends ModelBase {
  data_type: DataType = "loot_table";
  entries: LootEntry[] = [];
  
  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.entries = obj.entries ? obj.entries.map((o: any) => new LootEntry(o)) : [];
    }
  }

  toDBObj(): any {
    const base_db_obj = super.toDBObj();
    return {
      ...base_db_obj,
      entries: this.entries.map(o => o.toDBObj()),
    };
  }

  clone(drop_ids = false): LootTable {
    const new_obj = new LootTable();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }

  copy(copyMe: LootTable, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.entries = copyMe.entries.map(o => o.clone());
  }
}

export const LOOT_ITEM_SOURCES = ["Items", "Specific Item", "Loot Table"] as const;
export type LootItemSource = typeof LOOT_ITEM_SOURCES[number];

/**
 * One kind of thing an entry gives, rolled count times:
 * - Items: picked at random from what fits item_limit (and the value range, for things like gems and art objects)
 * - Specific Item: always item_id
 * - Loot Table: rolls on another loot table, picking its entries by weight (like the DMG's Magic Item Tables)
 */
export class LootItemGroup {
  true_id = "";
  source: LootItemSource = "Items";
  count = new DiceRoll({ size: 1, count: 1 });
  item_limit = new ItemLimitation();
  min_value = -1; // In gp. -1 means no minimum.
  max_value = -1; // In gp. -1 means no maximum.
  item_id = ""; // PseudoItem id, for Specific Item
  loot_table_id = ""; // For Loot Table

  constructor(obj?: any) {
    this.true_id = obj && obj.true_id ? obj.true_id : uuidv4().toString();
    if (obj) {
      this.source = obj.source ? obj.source : "Items";
      this.count = obj.count ? new DiceRoll(obj.count) : new DiceRoll({ size: 1, count: 1 });
      this.item_limit = obj.item_limit ? new ItemLimitation(obj.item_limit) : new ItemLimitation();
      this.min_value = obj.min_value !== undefined ? obj.min_value : -1;
      this.max_value = obj.max_value !== undefined ? obj.max_value : -1;
      this.item_id = obj.item_id ? obj.item_id : "";
      this.loot_table_id = obj.loot_table_id ? obj.loot_table_id : "";
    }
  }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      source: this.source,
      count: this.count.toDBObj(),
      item_limit: this.item_limit.toDBObj(),
      min_value: this.min_value,
      max_value: this.max_value,
      item_id: this.item_id,
      loot_table_id: this.loot_table_id,
    };
  }

  clone(): LootItemGroup {
    const new_obj = new LootItemGroup();
    new_obj.copy(this);
    return new_obj;
  }

  copy(copyMe: LootItemGroup): void {
    this.true_id = copyMe.true_id;
    this.source = copyMe.source;
    this.count = copyMe.count.clone(false);
    this.item_limit = copyMe.item_limit.clone();
    this.min_value = copyMe.min_value;
    this.max_value = copyMe.max_value;
    this.item_id = copyMe.item_id;
    this.loot_table_id = copyMe.loot_table_id;
  }
}

/**
 * When rolling, an entry is only a possibility if it fits the encounter:
 * its difficulty, its highest creature CR, its Environments, and the creatures in it.
 * Empty/-1 values mean it fits anything for that part.
 * Guaranteed entries are given once whenever they fit (like a hoard's coins),
 * and the rest are picked by weight for each roll.
 * Each entry gives its item groups and money.
 */
export class LootEntry {
  true_id = "";
  name = ""; // A label for the entry, like "Rare Wands", so big tables are easier to manage
  weight = 1; // Relative chance of being picked compared to the other entries that fit
  guaranteed = false; // Given once whenever it fits, instead of being one of the weighted rolls
  environment_ids: string[] = []; // Environments it makes sense to find this in. Empty means anywhere.
  difficulties: string[] = []; // Encounter difficulties (Easy, Medium, Hard, Deadly) this can come from. Empty means any.
  min_cr = -1; // The highest CR of the creatures in the encounter has to be at least this. -1 means no minimum.
  max_cr = -1; // The highest CR of the creatures in the encounter can't be more than this. -1 means no maximum.
  creature_limit = new CreatureLimitation(); // Kinds of creatures this entry can drop from. At least one creature in the encounter has to fit, unless it's empty.
  item_groups: LootItemGroup[] = []; // What items it gives. I'm going to make a Crafting Component item type, which will include things that make sense to be body parts of creatures or magic bits that make sense to be found on/in them.
  money = new RandomMoney(); // Money that is found alongside the items.
  
  constructor(obj?: any) {
    this.true_id = obj && obj.true_id ? obj.true_id : uuidv4().toString();
    if (obj) {
      this.name = obj.name ? obj.name : "";
      this.weight = obj.weight !== undefined ? obj.weight : 1;
      this.guaranteed = obj.guaranteed ? obj.guaranteed : false;
      this.environment_ids = obj.environment_ids ? [...obj.environment_ids] : [];
      this.difficulties = obj.difficulties ? [...obj.difficulties] : [];
      this.min_cr = obj.min_cr !== undefined ? obj.min_cr : -1;
      this.max_cr = obj.max_cr !== undefined ? obj.max_cr : -1;
      this.creature_limit = obj.creature_limit ? new CreatureLimitation(obj.creature_limit) : new CreatureLimitation();
      if (obj.item_groups) {
        this.item_groups = obj.item_groups.map((o: any) => new LootItemGroup(o));
      } else if (obj.item_limit) {
        // Entries used to have a single item_limit and item_count
        this.item_groups = [new LootItemGroup({ source: "Items", item_limit: obj.item_limit, count: obj.item_count })];
      }
      this.money = obj.money ? new RandomMoney(obj.money) : new RandomMoney();
    }
  }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      name: this.name,
      weight: this.weight,
      guaranteed: this.guaranteed,
      environment_ids: this.environment_ids,
      difficulties: this.difficulties,
      min_cr: this.min_cr,
      max_cr: this.max_cr,
      creature_limit: this.creature_limit.toDBObj(),
      item_groups: this.item_groups.map(o => o.toDBObj()),
      money: this.money.toDBObj(),
    };
  }

  clone(): LootEntry {
    const new_obj = new LootEntry();
    new_obj.copy(this);
    return new_obj;
  }

  copy(copyMe: LootEntry): void {
    this.true_id = copyMe.true_id;
    this.name = copyMe.name;
    this.weight = copyMe.weight;
    this.guaranteed = copyMe.guaranteed;
    this.environment_ids = [...copyMe.environment_ids];
    this.difficulties = [...copyMe.difficulties];
    this.min_cr = copyMe.min_cr;
    this.max_cr = copyMe.max_cr;
    this.creature_limit = copyMe.creature_limit.clone();
    this.item_groups = copyMe.item_groups.map(o => o.clone());
    this.money = copyMe.money.clone();
  }
}

export class RandomMoney {
  pp = new DiceRoll();
  gp = new DiceRoll();
  ep = new DiceRoll();
  sp = new DiceRoll();
  cp = new DiceRoll();

  isEmpty() {
    return [this.pp, this.gp, this.ep, this.sp, this.cp].every(o => o.count === 0);
  }
  
  constructor(obj?: any) {
    if (obj) {
      this.pp = obj.pp ? new DiceRoll(obj.pp) : new DiceRoll();
      this.gp = obj.gp ? new DiceRoll(obj.gp) : new DiceRoll();
      this.ep = obj.ep ? new DiceRoll(obj.ep) : new DiceRoll();
      this.sp = obj.sp ? new DiceRoll(obj.sp) : new DiceRoll();
      this.cp = obj.cp ? new DiceRoll(obj.cp) : new DiceRoll();
    }
  }

  toDBObj(): any {
    return {
      pp: this.pp.toDBObj(),
      gp: this.gp.toDBObj(),
      ep: this.ep.toDBObj(),
      sp: this.sp.toDBObj(),
      cp: this.cp.toDBObj(),
    };
  }

  clone(): RandomMoney {
    const new_obj = new RandomMoney();
    new_obj.copy(this);
    return new_obj;
  }

  copy(copyMe: RandomMoney): void {
    this.pp = copyMe.pp.clone(false);
    this.gp = copyMe.gp.clone(false);
    this.ep = copyMe.ep.clone(false);
    this.sp = copyMe.sp.clone(false);
    this.cp = copyMe.cp.clone(false);
  }
}